import { useCallback, useState } from "react";

import { StorageKey, readStored, writeStored } from "../../lib/storage";

/**
 * Which sidebar rows are open. Persisted, because a tree that forgets its shape
 * on every navigation is a tree the reader has to rebuild every time.
 */
export function useExpanded(): {
  expanded: ReadonlySet<string>;
  toggle: (id: string) => void;
  isExpanded: (id: string) => boolean;
} {
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const stored = readStored(StorageKey.sidebarExpanded);
    if (stored === null) return new Set();
    try {
      const parsed: unknown = JSON.parse(stored);
      return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : []);
    } catch {
      return new Set();
    }
  });

  const toggle = useCallback((id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeStored(StorageKey.sidebarExpanded, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const isExpanded = useCallback((id: string) => expanded.has(id), [expanded]);

  return { expanded, toggle, isExpanded };
}
